import React, { useEffect, useState, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import Button from "components/Button";
import { UserContext } from "context/user";
import { getContactDetails } from "api/User";

const StripeCardElement = ({ amount, createPaymentIntent, order }) => {
  const elements = useElements();
  const stripe = useStripe();
  const history = useHistory();
  const [status, setStatus] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const { token } = useContext(UserContext);

  const [billingDetails, setBillingDetails] = useState({
    name: "",
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    zipCode: "",
    country: "",
    state: "",
  });

  useEffect(() => {
    if (status === "Success") {
      history.push({
        pathname: `/order-review/${order?.id}/success`,
        state: { token },
      });
    }
  }, [status, history, token, order]);

  const fetchData = useCallback(async () => {
    if (!token) return;

    const userDetailsData = await getContactDetails(token);
    setUserDetails(userDetailsData.data);
  }, [token]);

  useEffect(() => {
    if (userDetails) {
      setBillingDetails({
        name: userDetails?.first_name,
        companyName: userDetails?.company,
        address1: userDetails?.address1,
        address2: userDetails?.address2,
        city: userDetails?.city,
        zipCode: userDetails?.post_code,
        country: userDetails?.country,
        state: userDetails?.state,
        first_name: userDetails?.first_name,
        last_name: userDetails?.last_name,
      });
    }
  }, [userDetails]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("Processing");

    if (!stripe || !elements) return;

    try {
      const payment = await createPaymentIntent();

      const paymentMethodRequest = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
        billing_details: {
          address: {
            city: billingDetails?.state,
            country: billingDetails?.country,
            line1: billingDetails?.streetAddress,
            line2: billingDetails?.suburb,
            postal_code: billingDetails?.zipCode,
          },
          name: `${userDetails?.first_name} ${userDetails?.last_name}`,
        },
      });

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        payment.data.client_secret,
        {
          payment_method: paymentMethodRequest?.paymentMethod?.id,
        }
      );

      if (paymentIntent?.status === "succeeded") {
        setStatus("Success");
      }

      if (error) {
        setStatus("Error");
      }
    } catch {
      setStatus("Error");
    }
  };

  // if (status === "Processing") {
  //   return (
  //     <div className="group-wrapper">
  //       <p className="title">Processing Payment</p>
  //       <p className="description">
  //         It may take a few seconds to process your payment, Please wait.
  //       </p>
  //       <span className="icon-refresh" />
  //     </div>
  //   );
  // }

  if (status === "Success") {
    return (
      <>
        <div className="bubble-wrap -success">
          Payment is successful. You will be redirected within a few seconds.
        </div>
        <div className="group-wrapper">
          <p className="title">Payment Successful</p>
          <span className="icon-check" />
        </div>
        <div className="button-group">
          <Button
            onClick={() => history.push("/order")}
            label="View Orders"
            type="outlined"
            color="white"
            block
          />
        </div>
      </>
    );
  }

  return (
    <>
      {status === "Error" && (
        <div className="bubble-wrap -error">
          Your card has been declined. Please check your card information and
          try it again.
        </div>
      )}
      <div className="group-wrapper">
        <p className="title -input">Payment Information</p>
        <form id="payment-form" onSubmit={handleSubmit}>
          <CardElement options={{ hidePostalCode: true }} />
          <div className="button-group">
            <Button
              label={`Pay $${amount}`}
              type="solid"
              color="blue"
              block
              submit
              disabled={
                order.status === "processing" || order.status === "done"
              }
              loading={status === "Processing"}
            />
            <Button
              onClick={() => history.push("/order")}
              label="View Orders"
              type="outlined"
              color="white"
              block
            />
          </div>
        </form>
      </div>
    </>
  );
};

StripeCardElement.propTypes = {
  amount: PropTypes.number.isRequired,
  createPaymentIntent: PropTypes.func.isRequired,
  order: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
};

export default StripeCardElement;
